/**
 * Apply cooling system list edits from a Request Detail edit session on Save Changes (#996 Phase 2).
 * Mirrors smallUpsEditSessionFlush. Note PUT (not PATCH) for unit updates, and the
 * reorder body key is `coolingSystemIds`.
 */

import { api } from './api';
import {
  computeRepeatableOperations,
  type ComputedRepeatableOps,
} from './computeRepeatableOperations';
import { mirrorCoolingSystemToOfflineRequestCache } from './coolingSystemOfflineSync';
import { toCreateCoolingSystemPayload } from './coolingSystemPayload';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { flushRepeatableInstanceAttachmentSectionRemaps } from './repeatableInstanceAttachmentRemapFlush';
import { deleteRepeatableUnitTolerating404 } from './repeatableUnitDelete';
import {
  LEGACY_COOLING_SYSTEM_SYNTHETIC_ID,
  validateSessionCoolingSystemNames,
} from '../components/coolingsystems/coolingSystemListUtils';
import { COOLING_SYSTEM_REPEATABLE_ATTACHMENT_BASE_SECTIONS } from '../utils/repeatableInstanceAttachmentSections';

import type { CoolingSystemConfiguration } from '../types/coolingSystem';

function isPersistedCoolingSystemId(id: number | undefined): id is number {
  return id != null && id > 0;
}

function isSessionOnlyCoolingSystemId(id: number | undefined): boolean {
  return id == null || id <= 0 || id === LEGACY_COOLING_SYSTEM_SYNTHETIC_ID;
}

/**
 * Canonicalize for content comparison: sort keys and drop undefined values so
 * two rows with identical CONTENT compare equal regardless of key insertion
 * order. Load-bearing since #997 Story 6.6 (mirror of `largeUpsEditSessionFlush`):
 * the engine save bridge's baseline (raw API/mapper rows, identity keys first)
 * and the session rows (round-tripped through `descriptorInstanceToCoolingSystem`,
 * identity keys appended last) are different object lineages — a raw
 * `JSON.stringify` flagged every unit as changed, PUTting the whole list on save.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const entry = (value as Record<string, unknown>)[key];
      if (entry !== undefined) {
        sorted[key] = canonicalize(entry);
      }
    }
    return sorted;
  }
  return value;
}

function coolingSystemContentKey(unit: CoolingSystemConfiguration): string {
  // `requestId` excluded too (#997 Story 6.6, mirror large UPS/rack review): the
  // engine conversion stamps `parseInt('o-…') → 0` on offline-request session
  // rows, which would keep every untouched unit permanently "changed" against
  // the cached baseline.
  const {
    id: _id,
    requestId: _rid,
    createdAt: _ca,
    updatedAt: _ua,
    ...rest
  } = unit;
  return JSON.stringify(canonicalize(rest));
}

function coolingSystemEqual(
  a: CoolingSystemConfiguration[],
  b: CoolingSystemConfiguration[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (unit, index) =>
      coolingSystemContentKey(unit) === coolingSystemContentKey(b[index]),
  );
}

export function coolingSystemEditSessionHasChanges(
  baseline: CoolingSystemConfiguration[],
  session: CoolingSystemConfiguration[],
): boolean {
  return !coolingSystemEqual(baseline, session);
}

/**
 * Pure save-time diff for cooling systems (#1501): consumed by this flush AND
 * by the aggregate save payload builder — one diff implementation, two
 * consumers.
 */
export function computeCoolingSystemOperations(
  baseline: CoolingSystemConfiguration[],
  session: CoolingSystemConfiguration[],
): ComputedRepeatableOps<CoolingSystemConfiguration> {
  return computeRepeatableOperations(baseline, session, {
    isPersistedId: isPersistedCoolingSystemId,
    isSessionOnlyId: isSessionOnlyCoolingSystemId,
    contentKey: coolingSystemContentKey,
    stripForUpdate: unit => {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        requestId: _rid,
        ...data
      } = unit;
      return data;
    },
  });
}

export async function flushCoolingSystemEditSession(
  requestId: string,
  baseline: CoolingSystemConfiguration[],
  session: CoolingSystemConfiguration[],
  isOfflineRequest: boolean,
): Promise<CoolingSystemConfiguration[]> {
  if (!coolingSystemEditSessionHasChanges(baseline, session)) {
    return session;
  }

  const nameError = validateSessionCoolingSystemNames(session);
  if (nameError) {
    throw new Error(nameError);
  }

  const idKey = String(requestId);
  const useOnlineApi = networkService.isConnected() && !isOfflineRequest;

  const ops = computeCoolingSystemOperations(baseline, session);

  for (const deletedId of ops.deletes) {
    if (useOnlineApi) {
      await deleteRepeatableUnitTolerating404(
        `/requests/${idKey}/cooling-systems/${deletedId}`,
      );
    } else {
      await offlineCache.addCoolingSystemToOfflineQueue(
        idKey,
        'delete',
        undefined,
        deletedId,
      );
    }
  }

  const createdIdMap = new Map<number, number>();

  for (const { tempId, row } of ops.creates) {
    const payload = toCreateCoolingSystemPayload({
      ...row,
      requestId: parseInt(idKey, 10) || row.requestId,
    });
    if (useOnlineApi) {
      const created = await api.post<CoolingSystemConfiguration>(
        `/requests/${idKey}/cooling-systems`,
        payload,
      );
      if (tempId != null && created.id) {
        createdIdMap.set(tempId, created.id);
      }
    } else {
      await offlineCache.addCoolingSystemToOfflineQueue(
        idKey,
        'create',
        payload,
        tempId,
      );
    }
  }

  for (const { id, data } of ops.updates) {
    if (useOnlineApi) {
      await api.put<CoolingSystemConfiguration>(
        `/requests/${idKey}/cooling-systems/${id}`,
        data,
      );
    } else {
      await offlineCache.addCoolingSystemToOfflineQueue(
        idKey,
        'update',
        data,
        id,
      );
    }
  }

  const tempIdByClientKey = new Map(
    ops.creates.map(c => [c.clientKey, c.tempId]),
  );
  const orderedPersistedIds = ops.orderRefs
    .map(ref => {
      if (typeof ref === 'number') {
        return ref;
      }
      const tempId = tempIdByClientKey.get(ref);
      return tempId != null && createdIdMap.has(tempId)
        ? createdIdMap.get(tempId)!
        : null;
    })
    .filter((id): id is number => id != null);

  if (
    orderedPersistedIds.length > 0 &&
    JSON.stringify(orderedPersistedIds) !==
      JSON.stringify(ops.baselineOrderInSession)
  ) {
    if (useOnlineApi) {
      await api.patch<CoolingSystemConfiguration[]>(
        `/requests/${idKey}/cooling-systems/reorder`,
        { coolingSystemIds: orderedPersistedIds },
      );
    } else {
      await offlineCache.addCoolingSystemToOfflineQueue(
        idKey,
        'reorder',
        undefined,
        undefined,
        orderedPersistedIds,
      );
    }
  }

  await flushRepeatableInstanceAttachmentSectionRemaps(
    idKey,
    createdIdMap,
    COOLING_SYSTEM_REPEATABLE_ATTACHMENT_BASE_SECTIONS,
    useOnlineApi,
  );

  const normalizedSession = session.map(unit => {
    if (unit.id != null && createdIdMap.has(unit.id)) {
      return { ...unit, id: createdIdMap.get(unit.id)! };
    }
    return unit;
  });

  if (!useOnlineApi) {
    await offlineCache.updateCachedRequestCoolingSystems(
      idKey,
      normalizedSession,
    );
  } else {
    await mirrorCoolingSystemToOfflineRequestCache(idKey, normalizedSession);
  }

  return normalizedSession;
}
